import { useState } from "react";
import axios from "axios";
import PublicHeader from "../components/PublicHeader";
import PublicFooter from "../components/PublicFooter";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Sparkles, Zap, AlertCircle, ArrowRight, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EXAMPLES = [
  "Quand je reçois un email contenant 'facture', je veux le classer automatiquement dans une base Notion et prévenir l'équipe finance sur Slack.",
  "Chaque lundi matin à 9h, envoie un récapitulatif des nouveaux leads de la semaine précédente à l'équipe commerciale sur Slack.",
  "Quand un message Slack mentionne 'incident', crée une page Notion avec le contenu et assigne-la au responsable technique.",
];

export default function Demo() {
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [workflow, setWorkflow] = useState(null);
  const [error, setError] = useState(null);

  const generate = async () => {
    if (!description.trim()) return toast.error("Décris d'abord ton processus.");
    setLoading(true);
    setError(null);
    setWorkflow(null);
    try {
      const { data } = await axios.post(`${API}/demo/generate`, { description });
      setWorkflow(data);
      toast.success("Workflow généré !");
    } catch (e) {
      setError(e?.response?.data?.detail || "Génération échouée");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <PublicHeader />

      <section className="max-w-6xl mx-auto px-6 py-16" data-testid="demo-page">
        <div className="mb-12">
          <span className="overline">Démo publique</span>
          <h1 className="font-display text-5xl lg:text-6xl font-medium tracking-tighter mt-4 max-w-3xl">
            Teste l'IA. Aucune inscription.
          </h1>
          <p className="text-lg text-zinc-600 mt-4 max-w-2xl">
            Décris un processus de ton entreprise, et Claude Sonnet 4.5 te génère le workflow FlowMind exécutable en quelques secondes.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Input */}
          <div className="bg-white border border-zinc-200 rounded-sm p-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl font-medium tracking-tight">Décris ton processus</h2>
              <Badge className="bg-black text-white rounded-sm font-mono text-[10px]">1 · description</Badge>
            </div>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={6}
              placeholder="Ex: Quand je reçois un email client contenant 'urgent', classe-le en priorité haute dans Notion et préviens-moi sur Slack."
              className="rounded-sm resize-none font-mono text-sm"
              data-testid="demo-description-textarea"
            />

            <div className="mt-4">
              <p className="overline mb-3">Exemples</p>
              <div className="space-y-2">
                {EXAMPLES.map((ex, i) => (
                  <button
                    key={i}
                    onClick={() => setDescription(ex)}
                    className="w-full text-left text-xs text-zinc-600 hover:text-black border border-zinc-200 rounded-sm p-3 hover:border-zinc-400 transition-colors"
                    data-testid={`demo-example-${i}`}
                  >
                    {ex}
                  </button>
                ))}
              </div>
            </div>

            <Button
              onClick={generate}
              disabled={loading}
              className="w-full mt-6 klein-bg text-white hover:bg-blue-700 rounded-sm h-11"
              data-testid="demo-generate-button"
            >
              {loading ? (
                <>Génération en cours...</>
              ) : (
                <><Sparkles size={16} className="mr-2"/> Générer le workflow</>
              )}
            </Button>
          </div>

          {/* Output */}
          <div className="bg-white border border-zinc-200 rounded-sm p-8 min-h-[500px]">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl font-medium tracking-tight">Workflow généré</h2>
              <Badge className="bg-black text-white rounded-sm font-mono text-[10px]">2 · résultat</Badge>
            </div>

            {!workflow && !loading && !error && (
              <div className="flex flex-col items-center justify-center h-80 text-center text-zinc-400">
                <Zap size={32} strokeWidth={1.5} className="mb-3"/>
                <p className="text-sm">Le workflow apparaîtra ici.</p>
              </div>
            )}

            {loading && (
              <div className="flex flex-col items-center justify-center h-80 text-center">
                <div className="w-8 h-8 border-2 border-zinc-200 border-t-black rounded-full animate-spin mb-3"></div>
                <p className="text-sm text-zinc-500">Claude Sonnet 4.5 réfléchit...</p>
              </div>
            )}

            {error && (
              <div className="flex flex-col items-center justify-center h-80 text-center text-red-600">
                <AlertCircle size={32} strokeWidth={1.5} className="mb-3"/>
                <p className="text-sm">{error}</p>
              </div>
            )}

            {workflow && (
              <div className="space-y-4" data-testid="demo-workflow-result">
                <div>
                  <p className="overline">Nom</p>
                  <h3 className="font-display text-xl font-medium mt-1">{workflow.name}</h3>
                  <p className="text-sm text-zinc-600 mt-1">{workflow.description}</p>
                </div>

                <div className="border-t border-zinc-200 pt-4">
                  <p className="overline mb-3">Déclencheur</p>
                  <div className="bg-[#FAFAFA] border border-zinc-200 rounded-sm p-3">
                    <p className="font-mono text-xs text-zinc-500">{workflow.trigger?.type}</p>
                    <pre className="text-xs text-zinc-700 mt-1 overflow-x-auto">{JSON.stringify(workflow.trigger?.config || {}, null, 2)}</pre>
                  </div>
                </div>

                <div className="border-t border-zinc-200 pt-4">
                  <p className="overline mb-3">Étapes ({workflow.steps?.length || 0})</p>
                  <div className="space-y-2">
                    {workflow.steps?.map((s, i) => (
                      <div key={s.id || i} className="flex items-center gap-3 p-3 border border-zinc-200 rounded-sm bg-[#FAFAFA]">
                        <div className="w-7 h-7 rounded-sm bg-black text-white flex items-center justify-center font-mono text-xs shrink-0">
                          {i + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium">{s.name}</p>
                          <p className="text-xs text-zinc-500 font-mono">{s.type}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="border-t border-zinc-200 pt-4 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm">
                    <Clock size={14} className="klein-text"/>
                    <span className="text-zinc-600">Temps économisé estimé</span>
                  </div>
                  <span className="font-mono font-semibold klein-text">{workflow.estimated_time_saved_minutes} min / exécution</span>
                </div>

                <Link to="/signup" className="block">
                  <Button className="w-full rounded-sm bg-black text-white hover:bg-zinc-800 h-11 mt-4" data-testid="demo-signup-cta">
                    Sauvegarder & déployer ce workflow <ArrowRight size={14} className="ml-2"/>
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
